import React, { useEffect, useState } from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import { AsyncSelect } from '../ReactSelect';
import { getNameLikeCondition } from '../../helpers/search';
import { gql } from '@/modes/company/gql';
import { gql as waterSupplier_gql } from '@/modes/waterSupplier/gql';
import { ResultOf } from 'gql.tada';
import { Merge } from '@/helpers/merge-type';

export const FETCH_TESTERS = gql(`
  query GetPickerTesters(
    $where: JSON
    $profileWhere: JSON
    $profileRequired: Boolean
    $orderBy: [ColumnOrder]
  ) {
    vendors(where: $where, limit: null, orderBy: $orderBy) {
      id
      profileId
      profile(where: $profileWhere, required: $profileRequired) {
        id
        name
        type
      }
      accountNumber
    }
  }
`);

const FETCH_TESTERS_FOR_COMPANY = waterSupplier_gql(`#graphql
  query GetPickerTestersForCompany(
    $where: JSON
    $profileWhere: JSON
    $profileRequired: Boolean
  ) {
    vendors(where: $where) {
      id
      profileId
      vendors(where: { status: 1 }) {
        status
        id
        profileId
        profile(where: $profileWhere, required: $profileRequired) {
          id
          name
          type
        }
      }
    }
  }
`);

export type Tester = Merge<
  ResultOf<typeof FETCH_TESTERS>['vendors'][number],
  ResultOf<
    typeof FETCH_TESTERS_FOR_COMPANY
  >['vendors'][number]['vendors'][number]
>;

type TesterPickerProps = {
  testerProfileId?: string | null;
  companyProfileId?: string | null;
  where?: any;
  onChange?: (tester: Tester | null) => void;
  placeholder?: string;
  isClearable?: boolean;
  autoFocus?: boolean;
};

const TesterPicker = ({
  testerProfileId,
  companyProfileId,
  where,
  onChange,
  placeholder,
  isClearable,
  autoFocus,
}: TesterPickerProps): JSX.Element => {
  where = where ?? {};
  const client = useApolloClient();

  const { error: itemError, data: itemData } = useQuery(FETCH_TESTERS, {
    variables: {
      where: {
        profileId: testerProfileId,
      },
    },
    skip: !testerProfileId || !!companyProfileId,
  });

  const { error: itemErrorWS, data: itemDataWS } = useQuery(
    FETCH_TESTERS_FOR_COMPANY,
    {
      variables: {
        where: { profileId: companyProfileId },
        profileWhere: { id: testerProfileId },
        profileRequired: true,
      },
      skip: !testerProfileId || !companyProfileId,
    }
  );

  const selected: Tester | undefined = companyProfileId
    ? itemDataWS?.vendors[0]?.vendors[0]
    : itemData?.vendors[0];

  if (itemError) {
    throw itemError;
  }

  if (itemErrorWS) {
    throw itemErrorWS;
  }

  const loadOptionsFunc = async (inputValue: string) => {
    let vendors: Tester[] = [];

    if (companyProfileId) {
      const res = await client.query({
        query: FETCH_TESTERS_FOR_COMPANY,
        fetchPolicy: 'network-only',
        variables: {
          profileWhere: inputValue
            ? {
                name: getNameLikeCondition(inputValue),
                type: 1,
              }
            : { type: 1 },
          profileRequired: true,
          where: { profileId: companyProfileId },
        },
      });
      vendors = res.data?.vendors[0]?.vendors ?? [];
    } else {
      const res = await client.query({
        query: FETCH_TESTERS,
        fetchPolicy: 'network-only',
        variables: {
          profileWhere: inputValue
            ? {
                name: getNameLikeCondition(inputValue),
              }
            : undefined,
          profileRequired: !!inputValue,
          where,
          orderBy: inputValue
            ? undefined
            : [{ col: 'profile.name', dir: 'ASC' }],
        },
      });

      vendors = res.data?.vendors ?? [];
    }

    if (vendors.length > 0) {
      if (!selected && vendors.length === 1) {
        onChange?.(vendors[0]);
      }
      const companies: Tester[] = [];
      const individuals: Tester[] = [];
      vendors.forEach((tester: Tester) => {
        if (tester.profile.type === 'COMPANY') {
          companies.push(tester);
        } else {
          individuals.push(tester);
        }
      });

      if (companies.length > 0 && individuals.length > 0) {
        return [
          {
            label: 'Individuals',
            options: individuals,
          },
          {
            label: 'Companies',
            options: companies,
          },
        ];
      }

      return vendors;
    }

    return [];
  };

  const [defaultOptions, setDefaultOptions] = useState<
    Awaited<ReturnType<typeof loadOptionsFunc>>
  >([]);

  useEffect(() => {
    loadOptionsFunc('').then((options) => {
      setDefaultOptions(options);
    });
  }, [companyProfileId]);

  return (
    <AsyncSelect
      autoFocus={autoFocus}
      cacheOptions
      value={selected ?? null}
      loadOptions={loadOptionsFunc}
      defaultOptions={defaultOptions}
      isClearable={isClearable}
      noOptionsMessage={() => null}
      menuPlacement="auto"
      onChange={(selected: Tester | null) => {
        if (!selected) {
          onChange?.(null);
        } else {
          onChange?.(selected);
        }
      }}
      placeholder={placeholder ?? 'Enter tester name...'}
      getOptionLabel={(tester) =>
        tester.profile.name +
        (tester.accountNumber ? ` (${tester.accountNumber})` : '')
      }
      getOptionValue={(tester) => tester.profileId}
    />
  );
};

export default TesterPicker;
